import { Result } from "@/api/types";
import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { dateFormat, useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { IImpact, ImpactMetricKey } from "@/pages/dashboard/utils/types";
import { filterSearchToGetFilter, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

interface GetEmployeeImpactTotals extends Result {
  result: {
    employee_impact: IImpact;
    company_impact: IImpact;
  };
}

export function useEmployeeImpactTotals({ metrics }: { metrics: ImpactMetricKey[] }) {
  const { startDate, endDate } = useDateRange();

  return useQuery({
    queryKey: ["dashboard", "employee", "totals", startDate, endDate, metrics],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetEmployeeImpactTotals>("/dashboard/employee/totals", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metrics: metrics.join(";"),
          },
        })
        .then((result) => result.data.result),
  });
}

export type EmployeeImpactPerTypeRow = {
  impact_type: string;
  name: string;
  unit: string;
  distance: number;
} & IImpact;

interface GetEmployeeImpactPerType extends Result {
  result: {
    total_rows: number;
    employee_impact: EmployeeImpactPerTypeRow[];
    company_employee_impact: IImpact;
  };
}

export function useEmployeeImpactPerType({
  pagination,
  filter,
  search,
  sort,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  return useQuery({
    queryKey: [
      "dashboard",
      "employee",
      "per_type",
      startDate,
      endDate,
      pagination,
      filter,
      search,
      sort,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetEmployeeImpactPerType>("/dashboard/employee/per_type", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metric,
          },
        })
        .then((result) => result.data.result),
  });
}

export function useEmployeeImpactPerTypeExport({
  pagination,
  filter,
  search,
  sort,
  metric,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  metric: ImpactMetricKey;
}) {
  const { startDate, endDate } = useDateRange();

  return useMutation({
    mutationFn: () =>
      axios
        .get<Result<{ url: string }>>("/dashboard/employee/per_type", {
          params: {
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            export: true,
            metric,
          },
        })
        .then((result) => result.data.result.url),
  });
}
